import type { NodeMetadata } from '@/services/canvas-mappers';

/**
 * Conviction — how grounded a node is, read from existing metadata.
 *
 * The canvas renders this as a three-register ladder: a guess (provisional)
 * reads lighter, a stated node sits at the neutral baseline, and an evidenced
 * node is inked and weighted. This is the single source of that classification;
 * BaseNode consumes it today (edges/panels can adopt it without diverging).
 */
export type ConvictionRegister = 'evidenced' | 'stated' | 'provisional';

export interface ConvictionInput {
  /** Node type (customer | job | … | assumption | market_signal). */
  type: string;
  metadata?: NodeMetadata;
}

/** The magnitude shown in a node's tick slot. `none` = no tick (provenance or nothing carries it). */
export type ConvictionTick =
  | { kind: 'ratio'; count: number; total: number }
  | { kind: 'sources'; count: number }
  | { kind: 'none' };

// MVP shortcut: conviction is derived from existing source / respondent /
// citation metadata; a future explicit `confidence` field would replace this
// heuristic without touching the call sites.
export function deriveConviction({ type, metadata }: ConvictionInput): ConvictionRegister {
  // An assumption is a guess by definition — provisional even when a respondent
  // voiced it, so the type check wins over source.
  if (type === 'assumption') return 'provisional';
  // A market signal is external research — an evidence overlay by definition.
  if (type === 'market_signal') return 'evidenced';

  const source = metadata?.source;
  // Synthesised from real dialog data (the ratio rides in metadata when captured).
  if (source === 'synthesis') return 'evidenced';
  // A single real respondent.
  if (source === 'dialog') return 'evidenced';

  // The seed idea (idea-capture) and hand-authored nodes are stated, not grounded.
  return 'stated';
}

/**
 * The presentation a node's conviction tick takes:
 *  - synthesis with a captured count/total → an N/M ratio bar
 *  - market signal with citations → a source count
 *  - everything else → none (a single dialog node leans on its provenance line)
 */
export function convictionTick({ type, metadata }: ConvictionInput): ConvictionTick {
  if (metadata?.source === 'synthesis') {
    const { respondentCount: count, respondentTotal: total } = metadata;
    if (typeof total === 'number' && total > 0 && typeof count === 'number') {
      return { kind: 'ratio', count, total };
    }
  }

  if (type === 'market_signal') {
    const count = metadata?.citations?.length ?? 0;
    if (count > 0) return { kind: 'sources', count };
  }

  return { kind: 'none' };
}
